import { useMemo, useRef, useEffect } from 'react';
import { ApiClient } from '@/lib/http/ApiClient';
import { useAuth } from '@/features/auth/hooks/useAuth';

/**
 * Returns a memoized ApiClient wired with the current user's token and refresh callback.
 * Returns null while AuthProvider is still initialising (tokenService not yet available).
 *
 * Uses a ref for the refresh callback so the ApiClient is not recreated whenever
 * AuthProvider re-renders and produces a new refresh function reference.
 */
export function useApiClient(): ApiClient | null {
  const { tokenService, refresh } = useAuth();

  // Keep refresh ref always current so the ApiClient closure never goes stale
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  });

  return useMemo(() => {
    if (!tokenService) return null;
    return new ApiClient(
      '',
      () => tokenService.getAccessToken(),
      async () => refreshRef.current()
    );
  }, [tokenService]);
}
